import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntityPageLayout } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { useAuth } from '@/hooks/useAuth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Offer } from '../types';

// Feature components
import { CreateOfferForm, EditOfferForm, OfferTable, OfferDetails } from '../components';
import { useOffers, useCreateOffer } from '../hooks/useOfferManagement';
import { type CreateOfferInput } from '../schemas/offerSchemas';

export function OffersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [detailsDrawer, setDetailsDrawer] = useState<{
    open: boolean;
    offer: Offer | null;
  }>({ open: false, offer: null });
  const isMobile = useIsMobile();
  const { hasPermission } = useAuth();

  // React Query hooks
  const { data, isLoading, error } = useOffers();
  const createOfferMutation = useCreateOffer();

  const offers = data?.offers || [];
  const totalCount = data?.total_count || 0;
  const activeCount = data?.active_offers_count || 0;
  const inactiveCount = data?.inactive_offers_count || 0;

  // Handle /new route
  useEffect(() => {
    if (location.pathname === '/offers/new') {
      if (hasPermission('offer_management:create')) {
        setIsCreateDrawerOpen(true);
      } else {
        // Redirect to main page if no permission
        navigate('/offers');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Handle drawer close - navigate back to main page
  const handleCreateDrawerChange = (open: boolean) => {
    setIsCreateDrawerOpen(open);
    if (!open && location.pathname === '/offers/new') {
      navigate('/offers');
    }
  };

  // Handle create offer
  const handleCreateOffer = async (values: CreateOfferInput) => {
    try {
      await createOfferMutation.mutateAsync(values);
      handleCreateDrawerChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  // Handle view details
  const handleViewDetails = useCallback((offer: Offer) => {
    setDetailsDrawer({ open: true, offer });
  }, []);

  // Render edit form for the drawer
  const renderEditForm = useCallback((offer: Offer, onClose: () => void) => {
    return <EditOfferForm offer={offer} onClose={onClose} formId="edit-form" />;
  }, []);

  return (
    <>
      <Helmet>
        <title>Offers - Urban Finch Admin</title>
      </Helmet>
      <EntityPageLayout
        title="Offers"
        description="Create and manage promotional offers"
        permission="offer_management:read"
        testIdPrefix="offers"
        isLoading={isLoading}
        error={
          error instanceof Error
            ? error
            : error
              ? new Error(String(error))
              : undefined
        }
        headerActions={
          <PermissionGate permission="offer_management:create">
            <Button
              onClick={() => handleCreateDrawerChange(true)}
              data-testid="create-offer-button"
            >
              Create Offer
            </Button>
          </PermissionGate>
        }
      >
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="offer-total-count"
                >
                  {totalCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="offer-active-count"
                >
                  {activeCount}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Inactive Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  data-testid="offer-inactive-count"
                >
                  {inactiveCount}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Offers Table with Search */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>All Offers</CardTitle>
                  <CardDescription>
                    Manage promotional offers for venues
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <OfferTable
                data={offers}
                isLoading={isLoading}
                error={error instanceof Error ? error : null}
                renderEditForm={
                  hasPermission('offer_management:update')
                    ? renderEditForm
                    : undefined
                }
                onViewDetails={handleViewDetails}
              />
            </CardContent>
          </Card>
        </div>

        {/* Create Offer Drawer */}
        <Drawer
          open={isCreateDrawerOpen}
          onOpenChange={handleCreateDrawerChange}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[500px] h-full'}>
            <DrawerHeader>
              <DrawerTitle>Create New Offer</DrawerTitle>
              <DrawerDescription>Add a new promotional offer</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              <CreateOfferForm
                onSubmit={handleCreateOffer}
                formId="create-offer-form"
              />
            </div>
            <DrawerFooter>
              <Button
                type="submit"
                form="create-offer-form"
                disabled={createOfferMutation.isPending}
                data-testid="offer-form-submit"
              >
                {createOfferMutation.isPending ? 'Creating...' : 'Create Offer'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCreateDrawerChange(false)}
                disabled={createOfferMutation.isPending}
                data-testid="offer-form-cancel"
              >
                Cancel
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        {/* View Details Drawer */}
        <Drawer
          open={detailsDrawer.open}
          onOpenChange={(open) => setDetailsDrawer({ open, offer: detailsDrawer.offer })}
          direction={isMobile ? 'bottom' : 'right'}
        >
          <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[600px] h-full'}>
            <DrawerHeader>
              <DrawerTitle>Offer Details</DrawerTitle>
              <DrawerDescription>View offer information and statistics</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              {detailsDrawer.offer && (
                <OfferDetails offer={detailsDrawer.offer} />
              )}
            </div>
            <DrawerFooter>
              <Button
                variant="outline"
                onClick={() => setDetailsDrawer({ open: false, offer: null })}
              >
                Close
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </EntityPageLayout>
    </>
  );
}
